/**
 * Supabase クライアント設定
 * サーバーサイドとクライアントサイドの両方で使用するSupabaseクライアント
 */

import {createBrowserClient, createServerClient} from "@supabase/ssr";
import {type AstroCookies} from "astro";
import type {Database} from "./types/database.types";

/**
 * Cloudflare Workers ランタイム環境変数
 * ミドルウェアでsetRuntimeEnv()を呼び出し、各リクエストで利用可能にする
 */
let _runtimeEnv: Record<string, string | undefined> | undefined;

/**
 * Cloudflare Workers のランタイム環境変数を設定
 * Astro middleware から呼び出す: setRuntimeEnv(locals.runtime?.env)
 */
export function setRuntimeEnv(env: Record<string, string | undefined> | undefined) {
  _runtimeEnv = env;
}

/**
 * 環境変数を取得するヘルパー
 * Cloudflare Workers → import.meta.env → process.env の順で取得
 */
function getEnv(key: string): string | undefined {
  // @ts-ignore: import.meta.env is only available in Astro/Vite environments
  return _runtimeEnv?.[key] || import.meta.env?.[key] || (typeof process !== 'undefined' ? process.env?.[key] : undefined);
}

/**
 * Supabase URL と Anon Key を取得（存在しない場合はエラー）
 */
function getPublicEnv(): { supabaseUrl: string; supabaseAnonKey: string } {
  const supabaseUrl = getEnv('PUBLIC_SUPABASE_URL');
  const supabaseAnonKey = getEnv('PUBLIC_SUPABASE_ANON_KEY');
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Supabase環境変数が設定されていません。PUBLIC_SUPABASE_URLとPUBLIC_SUPABASE_ANON_KEYを設定してください。");
  }
  return { supabaseUrl, supabaseAnonKey };
}

/**
 * ブラウザ用Supabaseクライアントを作成
 * Note: Astro/Vite環境で動作（import.meta.env）、Node.js環境ではprocess.envを使用
 */
export function createClient() {
  const { supabaseUrl, supabaseAnonKey } = getPublicEnv();

  return createBrowserClient<Database>(supabaseUrl, supabaseAnonKey, {
    realtime: {
      worker: true,
      heartbeatIntervalMs: 15_000,
    },
  });
}

/**
 * Cookie設定のオプション型
 */
export interface CookieOptions {
  maxAge?: number;
  domain?: string;
  sameSite?: "strict" | "lax" | "none";
}

/**
 * サーバー用Supabaseクライアントを作成
 * SSRでのCookie管理に対応（セキュリティ設定デフォルト適用）
 * Note: Astro/Vite環境で動作
 *
 * @param cookies - AstroCookiesインスタンス
 * @param cookieOptions - アプリ固有のCookie設定（オプション）
 */
export function getServerClient(cookies: AstroCookies, cookieOptions?: CookieOptions) {
  const { supabaseUrl, supabaseAnonKey } = getPublicEnv();

  // セキュリティデフォルト設定
  // Note: httpOnly: false はSupabaseのSSR推奨設定
  // ブラウザ側でのセッション復元に必要。セキュリティはCSP、PKCE、短期トークンで担保
  const secureDefaults = {
    path: "/",
    httpOnly: false,
    sameSite: "lax" as const,
    // @ts-ignore: import.meta.env is only available in Astro/Vite environments
    secure: typeof import.meta !== "undefined" ? import.meta.env?.PROD : (typeof process !== 'undefined' ? process.env?.NODE_ENV === "production" : false),
  };

  return createServerClient<Database>(supabaseUrl, supabaseAnonKey, {
    cookies: {
      get(key: string) {
        return cookies.get(key)?.value;
      },
      set(key: string, value: string, options: any) {
        // Supabaseのoptionsを尊重しつつ、セキュリティ設定をマージ
        cookies.set(key, value, {
          ...options, // Supabaseが指定するmaxAge/expiresなどを保持
          ...secureDefaults, // セキュリティ設定を上書き
          ...cookieOptions, // アプリ固有の設定で最終上書き
        });
      },
      remove(key: string, options: any) {
        // 削除にも同じpath属性が必要
        cookies.delete(key, {
          ...options,
          path: secureDefaults.path,
        });
      },
    },
  });
}

/**
 * API用Supabaseクライアントを作成
 * APIエンドポイントで使用
 * Note: Astro/Vite環境で動作
 */
export function getApiClient(request: Request, cookies: AstroCookies) {
  const { supabaseUrl, supabaseAnonKey } = getPublicEnv();

  const headersCookie = request.headers.get("cookie");
  const parsedCookies = new Map<string, string>();

  if (headersCookie) {
    headersCookie.split("; ").forEach((cookie) => {
      const [name, ...value] = cookie.split("=");
      parsedCookies.set(name, value.join("="));
    });
  }

  return createServerClient<Database>(supabaseUrl, supabaseAnonKey, {
    cookies: {
      get(key: string) {
        return parsedCookies.get(key) || cookies.get(key)?.value;
      },
      set(key: string, value: string, options: any) {
        cookies.set(key, value, options);
      },
      remove(key: string, options: any) {
        cookies.delete(key, options);
      },
    },
  });
}

/**
 * 管理者用Supabaseクライアントを作成（tokyo-u準拠：非同期版）
 * Service Role Keyを使用してRLSをバイパス
 * 注意: セキュリティ上、サーバーサイドのみで使用すること
 * Note: Astro/Vite環境で動作
 */
export async function getAdminClient() {
  const supabaseUrl = getEnv('PUBLIC_SUPABASE_URL');
  const supabaseServiceKey = getEnv('SUPABASE_SERVICE_ROLE_KEY');

  if (!supabaseUrl) {
    throw new Error("SUPABASE_URL環境変数が設定されていません。PUBLIC_SUPABASE_URLを設定してください。");
  }

  if (!supabaseServiceKey) {
    throw new Error("SUPABASE_SERVICE_ROLE_KEY環境変数が設定されていません。管理者操作にはService Role Keyが必要です。");
  }

  // サーバーサイドでのみ実行されることを確認
  if (typeof window !== "undefined") {
    throw new Error("getAdminClientはサーバーサイドでのみ使用可能です。クライアントサイドでService Role Keyを使用することはセキュリティリスクとなります。");
  }

  // 動的インポート（コード分割 + セキュリティ向上）
  const {createClient} = await import("@supabase/supabase-js");

  return createClient<Database>(supabaseUrl, supabaseServiceKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  });
}

/**
 * ブラウザ用Supabaseクライアントを取得（tokyo-u準拠）
 * Note: シングルトンではなく、createClient()のエイリアス
 * 呼び出すたびに新しいインスタンスを作成しますが、Supabase内部でセッション管理されます
 */
export function getBrowserClient() {
  return createClient();
}

/**
 * シングルトンインスタンス（非推奨）
 * @deprecated tokyo-u準拠のため、getBrowserClient()を使用してください。この関数は将来的に削除されます。
 */
let browserClient: ReturnType<typeof createClient> | undefined;

/**
 * @deprecated tokyo-u準拠のため、getBrowserClient()を使用してください。この関数は将来的に削除されます。
 */
export function getClient() {
  if (typeof window === "undefined") {
    throw new Error("ブラウザクライアントはサーバーサイドでは使用できません");
  }

  if (!browserClient) {
    browserClient = createClient();
  }

  return browserClient;
}
